// Dimension types for filtering
export type DimensionType =
  | "month"
  | "department"
  | "item"
  | "category"
  | "productionUnit"
  | "group"
  | "customer"
  | "customerGroup"
  | "country"
  | "vendor"
  | "brand"
  | "salesPerson"
  | "sector"
  | "sectorToplevel";

export const DIMENSION_LABELS: Record<DimensionType, string> = {
  month: "Month",
  department: "Department",
  item: "Item",
  category: "Category",
  productionUnit: "Production Unit",
  group: "Group",
  customer: "Customer",
  customerGroup: "Customer Group",
  country: "Country",
  vendor: "Vendor",
  brand: "Brand",
  salesPerson: "Sales Person",
  sector: "Sector",
  sectorToplevel: "Sector Toplevel",
};

// Metric types for value selection
export type MetricType =
  | "revenue"
  | "revenueDiffEuro"
  | "revenueDiffPercent"
  | "revenuePercentTotal"
  | "quantity"
  | "quantityDiffNumber"
  | "quantityDiffPercent"
  // Purchase-only metrics (cost/margin related)
  | "cost"
  | "costDiffEuro"
  | "costDiffPercent"
  | "profit"
  | "profitDiffEuro"
  | "profitDiffPercent"
  | "margin"
  | "marginDiffPercent";

export const METRIC_LABELS: Record<MetricType, string> = {
  revenue: "Revenue",
  revenueDiffEuro: "Revenue Diff €",
  revenueDiffPercent: "Revenue Diff %",
  revenuePercentTotal: "Revenue % Total",
  quantity: "Quantity",
  quantityDiffNumber: "Quantity Diff #",
  quantityDiffPercent: "Quantity Diff %",
  // Purchase-only metric labels
  cost: "Cost",
  costDiffEuro: "Cost Diff €",
  costDiffPercent: "Cost Diff %",
  profit: "Profit",
  profitDiffEuro: "Profit Diff €",
  profitDiffPercent: "Profit Diff %",
  margin: "Margin %",
  marginDiffPercent: "Margin Diff %",
};

export const METRIC_SHORT_LABELS: Record<MetricType, string> = {
  revenue: "Rev",
  revenueDiffEuro: "Rev Diff €",
  revenueDiffPercent: "Rev Diff %",
  revenuePercentTotal: "Rev % Tot",
  quantity: "Qty",
  quantityDiffNumber: "Qty Diff #",
  quantityDiffPercent: "Qty Diff %",
  cost: "Cost",
  costDiffEuro: "Cost Diff €",
  costDiffPercent: "Cost Diff %",
  profit: "Profit",
  profitDiffEuro: "Profit Diff €",
  profitDiffPercent: "Profit Diff %",
  margin: "Margin %",
  marginDiffPercent: "Margin Diff %",
};

// Liquidation filter
export type LiquidationFilter = "all" | "only" | "none";

export type ExceptionFilter = "all" | "only" | "none";

// Time period types
export type TimePeriodType = "ytd" | "periodic";

export interface AnalyticsSettings {
  selectedMetrics: MetricType[];
  liquidationFilter: LiquidationFilter;
  exceptionFilter: ExceptionFilter;
  timePeriod: TimePeriodType;
  selectedCountries: string[];
  selectedSalesPeople: string[];
}

// Year labels
export type YearType = "CY" | "PY" | "PPY";

// Filter breadcrumb item
export interface BreadcrumbItem {
  dimension: DimensionType;
  value: string;
  label: string;
}

// Grid row data
export interface GridRow {
  id: string;
  dimensionValue: string;
  dimensionLabel: string;
  metrics: {
    [K in YearType]: {
      [M in MetricType]?: number;
    };
  };
}

// Sort configuration
export interface SortConfig {
  column: string;
  direction: "asc" | "desc";
}

// App state
export interface AppState {
  selectedMetrics: MetricType[];
  currentDimension: DimensionType;
  filters: BreadcrumbItem[];
  liquidationFilter: LiquidationFilter;
  exceptionFilter: ExceptionFilter;
  timePeriod: TimePeriodType;
  sortConfig: SortConfig | null;
}

// Re-export role types
export * from "./roles";
